/**
 * TosGate — full-screen modal that forces existing users to accept the
 * current Terms of Service + Privacy Policy the next time they log in
 * (once per version bump). New signups accept during the register form
 * itself and are already current; this only kicks in for legacy accounts
 * or after a `TOS_VERSION` bump.
 *
 * Mount once, at app root, inside AuthProvider.
 */

import { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../lib/firebase";
import { TOS_VERSION, LEGAL_URLS } from "../lib/legalVersions";
import type { UserProfile } from "../types";

export default function TosGate() {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [checked, setChecked] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const zh = typeof navigator !== "undefined" && navigator.language.startsWith("zh");

  // Load profile when a user signs in. Re-runs on uid change.
  useEffect(() => {
    if (authLoading || !user) { setProfile(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "profiles", user.uid));
        if (cancelled) return;
        if (snap.exists()) {
          setProfile({ id: snap.id, ...(snap.data() as Omit<UserProfile, "id">) });
        } else {
          setProfile(null);
        }
      } catch {
        if (!cancelled) setProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  const needsAcceptance = !!(
    user &&
    profile &&
    profile.tosVersion !== TOS_VERSION
  );

  if (!needsAcceptance) return null;

  const submit = async () => {
    if (!checked || !user) return;
    setSaving(true);
    setErr("");
    try {
      await updateDoc(doc(db, "profiles", user.uid), {
        tosVersion: TOS_VERSION,
        tosAcceptedAt: serverTimestamp(),
        marketingOptIn: marketing,
        ...(marketing ? { marketingOptInAt: serverTimestamp() } : {}),
      });
      // Local optimistic update to hide the modal.
      setProfile((p) => (p ? { ...p, tosVersion: TOS_VERSION } : p));
    } catch (e: any) {
      setErr(e?.message || (zh ? "保存失败" : "Save failed"));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl">
        <div className="p-5">
          <div className="text-lg font-bold text-court-blue-dark mb-1">
            {zh ? "服务条款已更新" : "Terms of Service Updated"}
          </div>
          <div className="text-xs text-on-surface-variant mb-4">
            {zh
              ? "我们更新了《服务条款》和《隐私政策》。请阅读并同意后继续使用 Acework。"
              : "We've updated our Terms of Service and Privacy Policy. Please review and accept to continue using Acework."}
          </div>

          <label className="flex items-start gap-2 p-3 rounded-lg border border-outline-variant/50 hover:bg-outline-variant/5 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1"
            />
            <span className="text-xs text-on-surface">
              {zh ? "我已阅读并同意 " : "I have read and agree to the "}
              <a
                href={LEGAL_URLS.terms}
                target="_blank"
                rel="noopener"
                className="text-court-blue-dark underline"
                onClick={(e) => e.stopPropagation()}
              >
                {zh ? "《服务条款》" : "Terms of Service"}
              </a>
              {zh ? " 和 " : " and "}
              <a
                href={LEGAL_URLS.privacy}
                target="_blank"
                rel="noopener"
                className="text-court-blue-dark underline"
                onClick={(e) => e.stopPropagation()}
              >
                {zh ? "《隐私政策》" : "Privacy Policy"}
              </a>
              <span className="text-error">*</span>
            </span>
          </label>

          <label className="flex items-start gap-2 p-3 rounded-lg hover:bg-outline-variant/5 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1"
            />
            <span className="text-xs text-on-surface">
              {zh
                ? "我同意接收活动通知和社区消息（可随时退订）"
                : "I consent to receive event and community messages (unsubscribe anytime)"}
            </span>
          </label>

          {err && (
            <div className="mb-3 text-xs text-error">{err}</div>
          )}

          <button
            onClick={submit}
            disabled={!checked || saving}
            className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm bg-court-blue-dark text-optic-yellow disabled:opacity-40 cursor-pointer"
          >
            {saving
              ? (zh ? "保存中…" : "Saving…")
              : (zh ? "同意并继续" : "Agree & Continue")}
          </button>

          <div className="mt-3 text-[10px] text-on-surface-variant text-center">
            v{TOS_VERSION}
          </div>
        </div>
      </div>
    </div>
  );
}
